import { useEffect, useState } from 'react';
import { useGET } from 'hooks/useRequest';
import { HookFormProvider } from 'components/cef_form/hook-form/providers/HookFormProvider';
import { HookFormStepProvider } from 'components/cef_form/hook-form/providers/HookFormStepProvider';
import useHookFormStepNavigation from 'components/cef_form/hook-form/hooks/useHookFormStepNavigation';
import {
  gaRegisterFormSteps,
  gaRegisterFormFieldChange,
  gaRegisterFormError,
} from 'data_layer/dataLayerRegisterForm';
import { Routes, Route, useNavigate } from 'react-router-dom';
import useFormConfig from './useFormConfig';
import steps from './formSteps';
import companyTypes from './companyTypes';
import getWatchedFields from './getWatchedFields';
import usePostcodeSelectors from './postcodeSelectors/usePostcodeSelectors';

const RegisterForm = ({ recaptchaKeys, bypassRecaptcha }) => {
  const {
    control,
    trigger,
    watch,
    setValue,
    handleSubmit,
    formState,
    setError,
    clearErrors,
    reset,
  } = useFormConfig();

  const navigate = useNavigate();

  const {
    currentStep,
    setCurrentStep,
    reachedStep,
    setReachedStep,
    handlePrevStep,
    handleNextStep,
  } = useHookFormStepNavigation(navigate);

  const stepComponents = Object.values(steps).map(step => step.component);

  const { title, hasNav } = steps[currentStep];

  const hasUnsavedChanges = formState.isDirty;
  useEffect(() => {
    // Add beforeunload event listener
    const handleBeforeUnload = event => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = ''; // Some browsers require returnValue to be set
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    gaRegisterFormError(formState.errors);
  }, [formState]);

  useEffect(() => {
    window.scrollTo(0, 0); // Always make sure top of the view shows when navigating between steps
  }, [currentStep]);

  const handleBlur = fieldName => {
    const fieldValue = watch(fieldName);
    gaRegisterFormFieldChange(fieldName, fieldValue); // Tell GA about a field change
  };

  const onSubmit = data => {
    Object.entries(data).forEach(([fieldName, fieldValue]) => {
      setValue(fieldName, fieldValue);
    });

    gaRegisterFormSteps(steps[currentStep].title, 'Complete'); // Tell GA the step is done
    handleNextStep();
    navigate(currentStep === 4 ? 'summary' : `step${currentStep + 1}`);
  };

  // Get business types dropdown data - consider moving
  // TODO: Rework error state for if endpoint fails after discussions with UX/backend
  const [businessTypeData, setBusinessTypeData] = useState(['loading']);
  const { data: types, error } = useGET('/company_details/business_types');
  useEffect(() => {
    if (error) {
      setBusinessTypeData([]);
    } else if (types && types.business_types) {
      const modifiedData = types.business_types.map(item => ({
        label: item[1],
        value: item[0],
      }));

      setBusinessTypeData(modifiedData);
    }
  }, [types, error]);

  // Get watched fields
  const watchedFields = getWatchedFields(watch);

  // Custom hook to deal with setting up validation states for postcode selectors
  const {
    dispatch,
    postcodeSearched,
    postcodeIsReset,
    hasPersistedData,
    addressIsDuplicated,
  } = usePostcodeSelectors(watchedFields, setValue);

  return (
    <HookFormProvider
      control={control}
      trigger={trigger}
      watch={watch}
      setValue={setValue}
      errors={formState.errors}
      setError={setError}
      clearErrors={clearErrors}
      reset={reset}
      handleBlur={handleBlur}
      // Pass unique values to the provider for this form only - allows us to keep it generic
      options={{
        companyTypes,
        recaptchaKeys,
        bypassRecaptcha,
        setCurrentStep,
        setReachedStep,
        businessTypeData,
        watchedFields,
        handlePrevStep,
        navigate,
        postcodeSelectorsValidation: {
          dispatch,
          postcodeSearched,
          postcodeIsReset,
          hasPersistedData,
          addressIsDuplicated,
        },
      }}>
      <Routes>
        {Object.keys(steps).map(step => {
          const Comp = stepComponents[step - 1];
          return (
            <Route
              key={`step${step}`}
              path={step === '5' ? 'summary' : `step${step}`}
              element={
                <HookFormStepProvider
                  navigate={navigate}
                  stepTitle={title}
                  hasNav={hasNav}
                  onSubmit={handleSubmit(onSubmit)}
                  reachedStep={reachedStep}>
                  <Comp />
                </HookFormStepProvider>
              }
            />
          );
        })}
      </Routes>
    </HookFormProvider>
  );
};

export default RegisterForm;
